import React from "react";
import { Card, SectionHeading, PrimaryButton } from "./ui";

const WHY_US = [
  {
    icon: "✦",
    title: "Accurate, Vedic-grade calculations",
    desc: "Every chart and match is computed precisely using authentic Vedic astrology methods, not guesswork.",
  },
  {
    icon: "🔒",
    title: "100% private & secure",
    desc: "Your birth details and payments are handled securely — nothing is shared without your consent.",
  },
  {
    icon: "🧑‍🏫",
    title: "Real, experienced astrologers & Pandit Jis",
    desc: "Talk to genuine astrologers and book verified Pandit Jis, not automated scripts.",
  },
  {
    icon: "⚡",
    title: "Instant reports, quick confirmations",
    desc: "Get your Kundli, birth chart, or match report in seconds — and pooja bookings confirmed on WhatsApp.",
  },
];

/**
 * Landing page for the app.
 *
 * `tabs` is the same TABS array App.jsx already builds for the nav bar
 * (minus the "home" tab itself), so every service tile here is generated
 * from the single source of truth in App.jsx — add a new tab there and it
 * automatically shows up on the homepage too.
 *
 * `onNavigate(key)` is just App.jsx's setActive, passed down so a tile
 * click switches tabs exactly the same way TabNav does.
 */
export default function HomePage({ tabs, onNavigate }) {
  return (
    <div style={styles.wrap}>
      {/* Hero */}
      <div style={styles.hero}>
        <div style={styles.heroEyebrow}>॥ वैदिक ज्योतिष ॥</div>
        <h1 style={styles.heroTitle}>Vedic Astrology, Computed Precisely</h1>
        <p style={styles.heroSubtitle}>
          Kundli matching, birth charts, live astrologer consultations, and pooja
          bookings — all grounded in your exact birth details, in one place.
        </p>
        <PrimaryButton onClick={() => tabs[0] && onNavigate(tabs[0].key)}>
          Explore Services
        </PrimaryButton>
      </div>

      {/* Services */}
      <div style={styles.section}>
        <SectionHeading
          eyebrow="What We Offer"
          title="Our Services"
          subtitle="Tap a service below to get started."
        />
        <div style={styles.grid}>
          {tabs.map((t) => (
            <button
              key={t.key}
              className="km-tab"
              style={styles.serviceCard}
              onClick={() => onNavigate(t.key)}
            >
              <span style={styles.serviceIcon}>{t.icon}</span>
              <span style={styles.serviceLabel}>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Why us */}
      <div style={styles.section}>
        <SectionHeading
          eyebrow="Why Choose Us"
          title="Trusted for accurate, judgement-free guidance"
        />
        <div style={styles.whyGrid}>
          {WHY_US.map((w, i) => (
            <Card key={i} style={styles.whyCard}>
              <div style={styles.whyIcon}>{w.icon}</div>
              <h3 style={styles.whyTitle}>{w.title}</h3>
              <p style={styles.whyDesc}>{w.desc}</p>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: { maxWidth: 1100, margin: "0 auto" },
  hero: { textAlign: "center", padding: "20px 16px 44px" },
  heroEyebrow: {
    fontFamily: "var(--font-serif)", color: "var(--terracotta)", fontSize: 14,
    letterSpacing: 2, marginBottom: 10,
  },
  heroTitle: {
    fontFamily: "var(--font-display)", fontSize: "clamp(28px, 5vw, 44px)",
    margin: "0 0 14px", color: "var(--ink)",
  },
  heroSubtitle: {
    fontSize: 15.5, color: "var(--ink-soft)", maxWidth: 560,
    margin: "0 auto 22px", lineHeight: 1.7,
  },
  section: { padding: "28px 16px" },
  grid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 14, maxWidth: 900, margin: "0 auto",
  },
  serviceCard: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    padding: "22px 14px", borderRadius: "var(--radius-lg)", border: "1px solid var(--line)",
    background: "var(--card)", cursor: "pointer",
  },
  serviceIcon: { fontSize: 26 },
  serviceLabel: { fontSize: 14, fontWeight: 700, color: "var(--ink)" },
  whyGrid: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16, maxWidth: 1000, margin: "0 auto",
  },
  whyCard: { maxWidth: "100%", textAlign: "center" },
  whyIcon: { fontSize: 28, marginBottom: 8 },
  whyTitle: { fontSize: 15.5, fontWeight: 700, color: "var(--ink)", margin: "0 0 6px" },
  whyDesc: { fontSize: 13.5, color: "var(--ink-soft)", lineHeight: 1.5, margin: 0 },
};
